import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { InlineWidget } from "react-calendly";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  Send,
  Sparkles,
  Users,
} from "lucide-react";

const CALENDLY_URL = "https://calendly.com/achiekobby17/test";

const cardBase =
  "group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border bg-white p-6 shadow-sm transition-all duration-300 sm:p-7";

/**
 * Interactive conversion section: ask → expert → book (Calendly).
 */
const BookAppointment = () => {
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);
  const calendlyRef = useRef(null);

  const handleGetAnswers = (e) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) {
      navigate("/contact");
      return;
    }
    navigate(`/contact?message=${encodeURIComponent(q)}`, {
      state: { prefilledMessage: q },
    });
  };

  const openBooking = () => {
    setShowCalendly(true);
    requestAnimationFrame(() => {
      calendlyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <section className="relative w-full overflow-hidden bg-white px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
      <div className="pointer-events-none absolute left-1/2 top-0 h-48 w-[min(100%,56rem)] -translate-x-1/2 bg-gradient-to-b from-[#FFD221]/12 to-transparent blur-2xl" aria-hidden />

      <div className="relative mx-auto max-w-7xl">
        <motion.div
          className="mx-auto mb-12 max-w-2xl text-center"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#FFD221]/35 bg-[#FFD221]/12 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[#9a7b00] shadow-sm">
            <Sparkles size={14} strokeWidth={2.5} className="text-[#E5B800]" aria-hidden />
            Interactive actions
          </span>
          <h2 className="text-[clamp(1.85rem,3.5vw+0.75rem,2.75rem)] font-black leading-tight tracking-tight text-gray-950">
            Get clarity{" "}
            <span className="bg-gradient-to-r from-[#b8860b] via-[#FFD221] to-[#ffec80] bg-clip-text text-transparent">
              your way
            </span>
          </h2>
          <p className="mt-3 text-[0.9375rem] leading-relaxed text-gray-500">
            Three direct paths — from a quick question to a booked consultation with a verified professional.
          </p>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
          {/* Ask a question */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            whileHover={{ y: -4 }}
            className={`${cardBase} border-black/[0.07] hover:border-[#FFD221]/50 hover:shadow-[0_24px_60px_-32px_rgba(0,0,0,0.35)]`}
          >
            <div className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[#c9a000]">
              <span className="text-base leading-none" aria-hidden>
                🔹
              </span>
              Ask a question
            </div>
            <p className="text-sm font-semibold leading-snug text-gray-800">
              Tell us what you&apos;re trying to figure out
            </p>

            <form onSubmit={handleGetAnswers} className="mt-5 flex min-h-0 flex-1 flex-col gap-4">
              <div
                className={`rounded-2xl border bg-[#f7f4ec] p-3 transition-[box-shadow,border-color] duration-300 ${
                  inputFocused
                    ? "border-black/20 shadow-[0_0_0_3px_rgba(255,210,33,0.35)]"
                    : "border-black/10"
                }`}
              >
                <label htmlFor="home-action-question" className="sr-only">
                  Your question
                </label>
                <textarea
                  id="home-action-question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  rows={4}
                  placeholder="e.g. I need to verify land documents before payment…"
                  className="min-h-[6.5rem] w-full resize-y bg-transparent text-sm font-medium text-gray-900 outline-none placeholder:text-gray-400"
                />
              </div>
              <motion.button
                type="submit"
                className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl bg-black py-3.5 text-sm font-black text-white shadow-md transition hover:bg-[#FFD221] hover:text-black"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Send size={16} strokeWidth={2.5} aria-hidden />
                Get Answers
                <ArrowRight size={16} strokeWidth={2.5} />
              </motion.button>
            </form>
          </motion.div>

          {/* Speak to an expert */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.08 }}
            whileHover={{ y: -4 }}
            className={`${cardBase} border-black/[0.07] hover:border-[#FFD221]/50 hover:shadow-[0_24px_60px_-32px_rgba(0,0,0,0.35)]`}
          >
            <div className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[#c9a000]">
              <span className="text-base leading-none" aria-hidden>
                🔹
              </span>
              Speak to an expert
            </div>
            <p className="text-sm font-semibold leading-snug text-gray-800">
              Connect directly with a verified professional
            </p>
            <div className="pointer-events-none absolute -right-8 top-20 h-32 w-32 rounded-full bg-[#FFD221]/15 blur-2xl transition group-hover:opacity-100" aria-hidden />

            <div className="mt-6 flex flex-1 flex-col justify-center">
              <div className="mb-6 flex justify-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-[#FFD221]/25 text-black shadow-inner ring-2 ring-[#FFD221]/40">
                  <Users size={36} strokeWidth={2} className="text-gray-900" />
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border-2 border-black/10 bg-white py-3.5 text-sm font-black text-gray-900 shadow-sm transition hover:border-[#FFD221] hover:bg-[#FFD221] hover:text-black"
              >
                <span aria-hidden>👉</span>
                Talk to an Expert
                <ArrowRight size={16} strokeWidth={2.5} className="transition group-hover:translate-x-0.5" />
              </Link>
              <p className="mt-4 text-center text-xs font-medium text-gray-400">
                Same care team — note if it&apos;s urgent in your message.
              </p>
            </div>
          </motion.div>

          {/* Book an appointment */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.16 }}
            whileHover={{ y: -4 }}
            className={`${cardBase} border-black/[0.07] hover:border-[#FFD221]/50 hover:shadow-[0_24px_60px_-32px_rgba(0,0,0,0.35)]`}
          >
            <div className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[#c9a000]">
              <span className="text-base leading-none" aria-hidden>
                🔹
              </span>
              Book an appointment
            </div>
            <p className="text-sm font-semibold leading-snug text-gray-800">
              Schedule a consultation at your convenience
            </p>

            <div className="mt-6 flex flex-1 flex-col justify-center">
              <div className="mb-6 flex justify-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-[#FFD221] to-[#ffdf44] text-black shadow-lg ring-2 ring-black/10">
                  <Calendar size={34} strokeWidth={2} />
                </div>
              </div>
              <motion.button
                type="button"
                onClick={openBooking}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFD221] py-3.5 text-sm font-black text-black shadow-md transition hover:bg-[#ffdf44]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span aria-hidden>👉</span>
                Book Appointment
                <ChevronDown
                  size={16}
                  strokeWidth={2.5}
                  className={`transition ${showCalendly ? "rotate-180" : ""}`}
                />
              </motion.button>
              <p className="mt-4 text-center text-xs font-medium text-gray-400">
                Opens the live scheduler below when you&apos;re ready.
              </p>
            </div>
          </motion.div>
        </div>

        <div ref={calendlyRef} id="book-appointment-calendly" className="scroll-mt-28">
          <AnimatePresence>
            {showCalendly && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-10 overflow-hidden rounded-[2rem] border border-black/10 bg-[#f7f4ec] shadow-[0_28px_80px_-48px_rgba(0,0,0,0.45)]">
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-black/10 bg-white px-6 py-4 sm:px-8">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFD221]">
                        <Calendar size={20} strokeWidth={2.5} className="text-black" />
                      </div>
                      <div>
                        <p className="text-xs font-black uppercase tracking-wider text-[#c9a000]">
                          Live scheduling
                        </p>
                        <p className="text-sm font-black text-gray-900">Pick a consultation slot</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCalendly(false)}
                      className="text-xs font-black uppercase tracking-wide text-gray-500 underline-offset-2 hover:text-black hover:underline"
                    >
                      Hide calendar
                    </button>
                  </div>
                  <div className="bg-[#fafaf8] p-2 sm:p-4">
                    <InlineWidget
                      url={CALENDLY_URL}
                      styles={{ height: "660px", minWidth: "280px" }}
                      pageSettings={{
                        hideEventTypeDetails: false,
                        hideLandingPageDetails: false,
                        backgroundColor: "fafaf8",
                        textColor: "111827",
                        primaryColor: "f5c518",
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default BookAppointment;
